import dayjs from 'dayjs'
import Image from 'next/image'
import React from 'react'

import { Button } from '@/legacy-src/components/ui/button'
import useEvents from '@/legacy-src/hooks/useEvents'
import useUser from '@/legacy-src/hooks/useUser'
import api from '@/legacy-src/services/api'
import { getTranslate } from '@/legacy-src/utilities/i18n'

import { groupEvents } from '@/src/domains/events/utils/groupEvents'

import Icon from '../icons/Icon'
import { APP_USER_ACTIVITY_TYPE } from './constants'
import useUserActivity from './useUserActivity'

const REVIEW_LINK = 'https://g.page/r/Cebjvlc6_sF3EBM/review'
const ASK_LATER_LIMIT_DAYS = 30

const AskForReviewInner: React.FC = () => {
  const { t } = getTranslate()
  const { isClient } = useUser()
  const { events: events, isLoading: isEventsLoading } = useEvents()
  const { futureEvents, pastEvents } = groupEvents(events)

  const { data: activities, refetchActivities, isLoading: isActivitiesLoading } = useUserActivity()

  const isCorrectNumberOfEvents = pastEvents?.length >= 2 || (pastEvents?.length >= 1 && futureEvents?.length >= 1)
  const isPermanentlyHidden = Boolean(
    activities?.filter((activity) =>
      [
        APP_USER_ACTIVITY_TYPE.ASK_FOR_REVIEW_CLICK_REPORT_PROBLEM,
        APP_USER_ACTIVITY_TYPE.ASK_FOR_REVIEW_CLICK_FIVE_STARS,
      ].includes(activity.type),
    ).length,
  )

  const isTemporarilyHidden = Boolean(
    activities?.filter(
      (activity) =>
        activity.type === APP_USER_ACTIVITY_TYPE.ASK_FOR_REVIEW_CLICK_LATER &&
        dayjs().isBefore(dayjs(activity.updatedAt).add(ASK_LATER_LIMIT_DAYS, 'days')),
    ).length,
  )

  const isSafariAgent = navigator?.userAgent.indexOf('Safari') !== -1
  const isChromeAgent = navigator?.userAgent.indexOf('Chrome') !== -1
  const isSafari = isChromeAgent ? false : isSafariAgent

  const isVisible =
    !isActivitiesLoading &&
    !isEventsLoading &&
    isClient &&
    isCorrectNumberOfEvents &&
    !isTemporarilyHidden &&
    !isPermanentlyHidden &&
    !isSafari

  const hideTemporarily = async () => {
    await api.setUserActivity(APP_USER_ACTIVITY_TYPE.ASK_FOR_REVIEW_CLICK_LATER)
    refetchActivities()
  }

  const createReview = async () => {
    await api.setUserActivity(APP_USER_ACTIVITY_TYPE.ASK_FOR_REVIEW_CLICK_FIVE_STARS)
    refetchActivities()
    window.open(REVIEW_LINK, '_blank', 'noopener noreferrer')
  }

  const reportProblem = async () => {
    await api.setUserActivity(APP_USER_ACTIVITY_TYPE.ASK_FOR_REVIEW_CLICK_REPORT_PROBLEM)
    refetchActivities()
    window.open(t('component.askForReview.link'), '_blank', 'noopener noreferrer')
  }

  if (!isVisible) return null

  return (
    <div className="fixed bottom-2 right-2 z-20 max-w-[300px] rounded-2xl bg-petrol-500 text-center text-white">
      <div className="flex w-full justify-end">
        <Button className="p-1 focus:shadow-none" variant="inline" onClick={hideTemporarily}>
          <Icon icon="cross" className="text-right" color="primary" size="md" />
        </Button>
      </div>
      <div className="px-6 pb-6">
        <div className="w-full">
          <Image priority alt="Roman" width="60" height="60" sizes="60px" className="" src="/images/roman.png" />
        </div>
        <div className="mb-4 text-sm font-bold">Roman</div>
        <div className="m-2 rounded-xl bg-blue-500 px-2 py-1 text-sm font-bold text-petrol-500">
          {t('component.askForReview.thankYou')}
        </div>
        <div className="mb-2 text-xl font-bold">{t('component.askForReview.title')}</div>
        <div className="mb-4 text-sm big-phone:text-base">{t('component.askForReview.description')}</div>

        <div className="mb-3">
          <Button onClick={createReview} size="small" className="w-full">
            {t('component.askForReview.rate')}
          </Button>
        </div>
        <div className="mb-3">
          <Button onClick={reportProblem} size="small" className="w-full">
            {t('component.askForReview.shareProblem')}
          </Button>
        </div>
        <div>
          <Button onClick={hideTemporarily} size="small" className="w-full">
            {t('component.askForReview.notNow')}
          </Button>
        </div>
      </div>
    </div>
  )
}

const AskForReview: React.FC = () => {
  const { isClient } = useUser()

  // since this needs to fetch all past events, we only load the component for clients
  return isClient ? <AskForReviewInner /> : null
}

export default AskForReview
